<script setup>
import { usePaginacionVenaScrapStore } from "@/store/paginacionStore";

const paginacionStore = usePaginacionVenaScrapStore();

defineProps({
  paginados: { type: Array, required: true },
  loading: { type: Boolean, required: true },
  paginaActual: { type: Number, required: true },
  totalPaginas: { type: Number, required: true },
  rangoInicio: { type: Number, required: true },
  rangoFin: { type: Number, required: true },
  totalRegistros: { type: Number, required: true },
});

const emit = defineEmits(["ver-detalle", "cambiar-pagina", "cambiar-por-pagina"]);

function formatFecha(value) {
  if (!value) return "—";
  const [y, m, d] = value.split("-");
  return `${d}/${m}/${y}`;
}
</script>

<template>
  <div class="d-xl-none">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-verde"></div>
    </div>
    <template v-else>
      <div
        v-for="item in paginados"
        :key="'mob-' + item.id"
        class="divisan-mobile-card mb-3 p-3 shadow-sm"
      >
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div class="fw-bold text-cafe">{{ item.operador_nombre }}</div>
            <div class="small text-muted-cafe">
              {{ formatFecha(item.fecha) }} · {{ item.maquina_nombre }}
            </div>
          </div>
          <button class="btn-circular" @click="emit('ver-detalle', item)">
            <i class="bi bi-eye"></i>
          </button>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <span class="badge-info">{{ item.producto_nombre }}</span>
          <span class="badge-info">{{ item.color_nombre }}</span>
          <span :class="item.con_aditivo ? 'badge-si' : 'badge-no'">
            {{ item.con_aditivo ? "Con aditivo" : "Sin aditivo" }}
          </span>
          <span class="badge-peso vena">Vena: {{ item.peso_vena_kg }} kg</span>
          <span class="badge-peso scrap">Scrap: {{ item.peso_scrap_kg }} kg</span>
        </div>
      </div>
      <div
        v-if="paginados.length === 0"
        class="text-center py-5 text-cafe opacity-50 fw-bold"
      >
        No se encontraron registros
      </div>
    </template>

    <!-- Paginación móvil -->
    <div class="paginacion-mobile">
      <div class="pag-mobile-top">
        <small class="pag-info"
          >{{ rangoInicio }}–{{ rangoFin }} de {{ totalRegistros }} registros</small
        >
        <div class="por-pagina-wrap">
          <label class="por-pagina-label">Filas:</label>
          <select
            class="por-pagina-select"
            :value="paginacionStore.porPagina"
            @change="emit('cambiar-por-pagina', Number($event.target.value))"
          >
            <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </div>
      </div>
      <div class="pag-mobile-nav">
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === 1"
          @click="emit('cambiar-pagina', paginaActual - 1)"
        >
          <i class="bi bi-chevron-left"></i> Anterior
        </button>
        <small class="text-verde fw-bold">{{ paginaActual }} / {{ totalPaginas }}</small>
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === totalPaginas"
          @click="emit('cambiar-pagina', paginaActual + 1)"
        >
          Siguiente <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.text-muted-cafe {
  color: var(--d-brown);
  opacity: 0.6;
  font-size: 0.9rem;
}
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: 15px;
  border-left: 6px solid var(--d-green);
}
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
  cursor: pointer;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.badge-info {
  display: inline-block;
  background: rgba(64, 34, 8, 0.08);
  color: var(--d-brown);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.78rem;
  font-weight: 600;
}
.badge-peso {
  display: inline-block;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 0.82rem;
  font-weight: 700;
}
.badge-peso.vena {
  background: rgba(96, 108, 56, 0.12);
  color: var(--d-green-dark);
}
.badge-peso.scrap {
  background: rgba(64, 34, 8, 0.1);
  color: var(--d-brown);
}
.badge-si {
  display: inline-block;
  background: #d4edda;
  color: #155724;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.8rem;
  font-weight: 700;
}
.badge-no {
  display: inline-block;
  background: #f8d7da;
  color: #721c24;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.8rem;
  font-weight: 700;
}
.paginacion-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
  padding: 10px 4px;
  margin-top: 4px;
}
.pag-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.pag-mobile-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  min-width: 58px;
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}
</style>
